import { isCrmPagePath } from '@/lib/crm-pages';
import { internalReturnUrl } from '@/lib/return-url';

/**
 * The single client entry point for this application's own API (02-auth-cutover Task 5).
 *
 * It replaces `fetchWithAuth`, which built a `Authorization: Bearer` header out of a
 * Cognito token the browser held. The credential is now the ambient HttpOnly session
 * cookie, so a request needs no header at all — which is exactly why every call site has
 * to come through here anyway: a raw `fetch('/api/…')` looks identical and silently
 * bypasses the two behaviours below.
 *
 *  - **401 → the localized login**, carrying the current internal path as `returnUrl`,
 *    and the 401 is still returned to the caller. A session that expires mid-visit
 *    becomes a login the user comes back from instead of a silently empty screen.
 *  - **503 `AUTH_DEPENDENCY_UNAVAILABLE` → one retry** after `Retry-After`, and never a
 *    logout. The 55-minute revalidation barrier answers this while Cognito is
 *    unreachable; a client that read any non-2xx as "logged out" would turn a provider
 *    blip into a platform-wide sign-out, which is the failure REQ-REVAL-04 exists to
 *    prevent.
 */

const DEFAULT_RETRY_AFTER_SECONDS = 1;
/** A server that asks for a long wait is not worth blocking a page render on. */
const MAX_RETRY_AFTER_SECONDS = 10;

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.location?.assign === 'function';
}

/** The path the user is on, query string included: dropping the search half is the usual
 *  way a `returnUrl` stops being a return. */
function currentInternalPath(): string | null {
  const location = window.location;
  return internalReturnUrl(`${location.pathname ?? ''}${location.search ?? ''}`);
}

function localeOf(): string {
  const segment = (window.location.pathname ?? '').split('/')[1];
  return ['en', 'fr', 'nl'].includes(segment) ? segment : 'en';
}

/** Sends the browser to the login page, coming back here after sign-in. */
export function redirectToLogin(): void {
  const login = `/${localeOf()}/auth/login`;
  // The login page is where a 401 SENDS people; redirecting it to itself is an
  // infinite reload, and the one page nobody can escape by signing in.
  if (window.location.pathname === login) return;
  const returnUrl = currentInternalPath();
  const query = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
  window.location.assign(`${login}${query}`);
}

const delay = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

/** The retryable outage, told apart from an ordinary 503 by the code the guard sends. */
async function isAuthDependencyOutage(response: Response): Promise<boolean> {
  if (response.status !== 503) return false;
  if (!(response.headers.get('cache-control') ?? '').includes('no-store')) return false;
  try {
    const body = await response.clone().json() as { code?: unknown };
    return body?.code === 'AUTH_DEPENDENCY_UNAVAILABLE';
  } catch {
    return false;
  }
}

function retryAfterMs(response: Response): number {
  const header = Number(response.headers.get('retry-after'));
  const seconds = Number.isFinite(header) && header > 0 ? header : DEFAULT_RETRY_AFTER_SECONDS;
  return Math.min(seconds, MAX_RETRY_AFTER_SECONDS) * 1000;
}

export interface ApiFetchOptions {
  /**
   * Whether a 401 sends the browser to the login page. Defaults to "yes, but only from
   * a CRM page": a public page — the widget, a shared valuation, the login page itself —
   * has anonymous visitors by design, and bouncing them into a staff login is a worse
   * failure than the unhandled 401. Set it explicitly for a call that must never
   * navigate, such as the session probe that ASKS whether anyone is signed in.
   */
  redirectOn401?: boolean;
}

function shouldRedirectOn401(options?: ApiFetchOptions): boolean {
  if (options?.redirectOn401 !== undefined) return options.redirectOn401;
  return isCrmPagePath(window.location.pathname ?? '');
}

export async function apiFetch(input: string, init?: RequestInit, options?: ApiFetchOptions): Promise<Response> {
  // `same-origin` rather than `include`: this client never talks to another origin, and
  // a cookie sent to one would be a credential leaving the application.
  const send = () => fetch(input, { ...init, credentials: 'same-origin' });

  let response = await send();

  if (await isAuthDependencyOutage(response)) {
    await delay(retryAfterMs(response));
    response = await send();
    // A second outage is reported to the caller as-is. It is still not a logout, so the
    // redirect below is deliberately not reached for it.
    if (await isAuthDependencyOutage(response)) return response;
  }

  // Server rendering has no browser to send anywhere; the caller gets the 401 and
  // decides. `redirect()` from a layout is the server-side answer (Task 6).
  if (response.status === 401 && isBrowser() && shouldRedirectOn401(options)) redirectToLogin();

  return response;
}
